import {
  hasDocumentReferenceComplete,
  isArtworkDelivered,
  getStates,
  hasActiveArtworkServices,
  getAvailableDeliveryToolsFromMRC as getAvailableDeliveryTools,
  DELIVERY_TOOLS,
} from '@printdeal/business-logic';
import { UseQueryResult } from '@tanstack/react-query';
import { stringArrayToObject } from '@printdeal/transformers';

// @ts-expect-error
import { canRemoveFile } from '../../../../../../helpers/order/Order';
import { Environment } from '../../../../../../helpers/env';
import { SanitizedLineItem, SanitizedOrder } from '../../../../../../types/order/Order';
import { formatAttributes } from '../../../../../../helpers/MerchantRulesHelper';
import useDeliveryTools from '../../../../../../hooks/useDeliveryTools';
import { dispatchAccountEvent } from '../../../../../../helpers/dataLayerHelpers/accountDataLayerHelper';
import useFileValidation from '../../../../../../hooks/useFileValidation';

const ReasonsDisabled = {
  ARTWORK_INVALID: 'Artwork invalid',
  ARTWORK_UNAVAILABLE: 'No artwork has been delivered',
  ARTWORK_UNKNOWN: 'The required documentRef is unavailable for the lineItem',
  CANNOT_EDIT_FILE: 'File cannot be edited due to lineItem state',
  DELIVERY_TOOLS_FAILED: 'The delivery tools could not be loaded',
  DELIVERY_TOOLS_EMPTY: 'There are no valid delivery tools available',
  FILES_INVALID: 'The currently available files are invalid',
  HAS_ARTWORK_SERVICE: 'Has art service',
  LOADING_DATA: 'The delivery tools or file validation are being loaded',
  NO_PRESELECTED_TOOL: 'No delivery tool has been selected', // This must be present, added when first uploading the existing artwork
  TOOL_INVALID: 'The delivery tool is no longer valid',
  TOOLS_UNAVAILABLE: 'Delivery tools unavailable',
};

const getDisabledConfigurationPayload = (
  reason?: string,
) => ({
  enabled: false,
  deliveryToolSettings: {},
  reason,
});

const states = getStates(Environment);

const needsFileValidation = (lineItem: SanitizedLineItem, order: SanitizedOrder) => {
  const { editorType: selectedDeliveryTool } = lineItem.custom.fields;

  return isArtworkDelivered(lineItem)
    && hasDocumentReferenceComplete(lineItem)
    && !hasActiveArtworkServices(lineItem.id, order, Environment)
    && canRemoveFile(lineItem, states.lineItem)
    && Boolean(selectedDeliveryTool);
};

const needsDeliveryTools = (
  lineItem: SanitizedLineItem,
  order: SanitizedOrder,
  fileValidation: UseQueryResult,
) => {
  const { editorType: selectedDeliveryTool } = lineItem.custom.fields;

  return isArtworkDelivered(lineItem)
    && hasDocumentReferenceComplete(lineItem)
    && !hasActiveArtworkServices(lineItem.id, order, Environment)
    && canRemoveFile(lineItem, states.lineItem)
    && Boolean(selectedDeliveryTool)
    && !fileValidation.isSuccess;
};

const useEditFiles = (lineItem: SanitizedLineItem, order: SanitizedOrder) => {
  const deliveryMethods: string[] = [];

  const { productKey, userSelection, editorType: selectedDeliveryTool } = lineItem.custom.fields;
  const attributes = formatAttributes(stringArrayToObject(userSelection));

  // The fileValidation API-call will not be done unless needsFileValidation() is true.
  const fileValidation = useFileValidation(
    lineItem,
    needsFileValidation(lineItem, order),
  );

  // The deliveryTools API-call will not be done unless needsDeliveryTools() is true.
  const deliveryTools = useDeliveryTools(
    productKey,
    attributes,
    needsDeliveryTools(lineItem, order, fileValidation),
  );

  if (!hasDocumentReferenceComplete(lineItem)) {
    return getDisabledConfigurationPayload(ReasonsDisabled.ARTWORK_UNAVAILABLE);
  }

  if (hasActiveArtworkServices(lineItem.id, order, Environment)) {
    return getDisabledConfigurationPayload(ReasonsDisabled.HAS_ARTWORK_SERVICE);
  }

  if (!canRemoveFile(lineItem, states.lineItem)) {
    // The customer cannot edit artwork after it has gone in production.
    return {
      enabled: false,
      reasonDisabled: ReasonsDisabled.CANNOT_EDIT_FILE,
    };
  }

  if (!selectedDeliveryTool) {
    // The customer cannot edit artwork after if we do not know which deliveryTool was used.
    // NOTE: I'm assuming that this is the reason why we currently exclude alternative upload, but we really shouldn't.
    return {
      enabled: false,
      reasonDisabled: ReasonsDisabled.NO_PRESELECTED_TOOL,
    };
  }

  // The customer still needs to deliver files themselves.
  // Validate the existing files and fetch the deliveryTools they might use to do so.
  if (!fileValidation || fileValidation.isLoading || deliveryTools.isLoading) {
    // While the tools are being fetched, display an appropriate visual.
    return {
      enabled: false,
      reasonDisabled: ReasonsDisabled.LOADING_DATA,
    };
  }

  if (fileValidation.isError
    || fileValidation?.data[lineItem.id]?.files.length === 0
    || !fileValidation?.data[lineItem.id]?.valid) {
    // The current artwork is invalid, and thus cannot be edited anymore.
    dispatchAccountEvent(
      'order_detail_api_failure',
      'validate_files_of_line_item',
      { lineItemId: lineItem.id, actionTriggeringValidation: 'editFiles' },
    );

    return {
      enabled: false,
      reasonDisabled: ReasonsDisabled.ARTWORK_INVALID,
    };
  }

  if (deliveryTools.isError || !deliveryTools.data) {
    // The delivery tools could not be retrieved.
    dispatchAccountEvent('order_detail_api_failure', 'get_delivery_tool_settings');

    return {
      enabled: false,
      reasonDisabled: ReasonsDisabled.DELIVERY_TOOLS_FAILED,
    };
  }

  // Determine which deliveryTools are valid for the given lineItem.
  const deliveryToolSettings = deliveryTools.data;
  const validDeliveryTools = getAvailableDeliveryTools(deliveryToolSettings);

  if (validDeliveryTools.length === 0) {
    // We don't have valid deliveryTools to edit the files.
    return {
      enabled: false,
      reasonDisabled: ReasonsDisabled.DELIVERY_TOOLS_EMPTY,
    };
  }

  if (!validDeliveryTools.includes(selectedDeliveryTool)) {
    // The selected deliveryTool is no longer valid
    return {
      enabled: false,
      reasonDisabled: ReasonsDisabled.TOOL_INVALID,
    };
  }

  if (selectedDeliveryTool === DELIVERY_TOOLS.UPLOADER_TOOL) {
    deliveryMethods.push(DELIVERY_TOOLS.UPLOADER_TOOL);
  } else {
    deliveryMethods.push('editor');
  }

  return {
    enabled: true,
    deliveryMethods,
    deliveryToolSettings,
  };
};

export default useEditFiles;
